import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  type GetCurrentUserOutput,
  getCurrentUser,
  type ResetPasswordOutput,
  resetPassword,
  type SignInOutput,
  signIn,
  signOut,
} from "aws-amplify/auth"
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito"
import { datadogRum } from "@datadog/browser-rum"
import config from "config"
import type { IChangeForgotPassword, IChangeUserPassword } from "types/userTypes"

class AuthService {
  resetPasswordSignin: SignInOutput | undefined

  public constructor() {
    // Amplify configuration is now handled in index.tsx before app initialization
    // This ensures proper initialization order for AWS Amplify v6
    this.resetPasswordSignin = undefined
  }

  public login = async (username: string, password: string): Promise<string> => {
    const res = await signIn({ username, password }).then((result) => {
      if (result.nextStep?.signInStep) {
        switch (result.nextStep.signInStep) {
          case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
            this.resetPasswordSignin = result
            return "NEW_PASSWORD_REQUIRED"
          case "RESET_PASSWORD":
            this.resetPasswordSignin = result
            return "RESET_PASSWORD"
          case "DONE":
            return "DONE"
          default:
            throw new Error("Something went wrong..")
        }
      }
      throw new Error("Something went wrong..")
    })

    if (res === "DONE") {
      const userId = await this.getUserId()
      if (userId) {
        datadogRum.setUser({ id: userId })
      }
    }

    return res
  }

  public changePassword = async (userData: IChangeUserPassword): Promise<boolean> => {
    var resp = await confirmSignIn({
      challengeResponse: userData.password,
      options: {
        userAttributes: {
          family_name: userData.lastname,
          given_name: userData.firstname,
          phone_number: userData.phonenumber,
        },
      },
    })

    authService.resetPasswordSignin = undefined
    return resp.nextStep?.signInStep === "DONE"
  }

  public logout = async () => {
    datadogRum.clearUser()
    await signOut()
  }

  public getUser = async (): Promise<GetCurrentUserOutput | undefined> => {
    const user = await getCurrentUser()
    return user
  }

  public getUserNameLetters = async (): Promise<string> => {
    const data = await fetchUserAttributes()
    if (data.given_name === undefined || data.family_name === undefined) {
      return ""
    }
    return data.given_name[0] + data.family_name[0]
  }

  public getRolesAssignedToUser = async (): Promise<Array<string> | undefined> => {
    const { tokens } = await fetchAuthSession({ forceRefresh: true })
    const roles = tokens?.accessToken.payload["cognito:groups"]
    if (!Array.isArray(roles)) return undefined
    return roles.map((x) => String(x))
  }

  private getCookie = (key: string): string | undefined => {
    const b = document.cookie.match(`(^|;)\\s*${key}\\s*=\\s*([^;]+)`)
    return b ? b.pop() : undefined
  }

  private getLocalStorageValue = (key: string): string | undefined => {
    const val = localStorage.getItem(key)
    return val ? val : undefined
  }

  public getRefreshToken = async (): Promise<string> => {
    const session = await fetchAuthSession({ forceRefresh: true })
    const sub = session.userSub
    const clientId = config.awsUserPoolClientId
    const key = `CognitoIdentityServiceProvider.${clientId}.${sub}.refreshToken`

    const tokens = await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens()
    let refreshToken = tokens?.refreshToken

    if (refreshToken === undefined) {
      refreshToken = this.getCookie(key)
    }
    if (refreshToken === undefined) {
      refreshToken = this.getLocalStorageValue(key)
    }

    return refreshToken ?? ""
  }

  public getAccessToken = async (): Promise<string> => {
    const session = await fetchAuthSession()
    return session.tokens?.accessToken?.toString() ?? ""
  }

  public changeForgotPassword = async (forgotUserPassword: IChangeForgotPassword) => {
    const result = await confirmResetPassword({
      username: forgotUserPassword.username,
      newPassword: forgotUserPassword.password,
      confirmationCode: forgotUserPassword.code,
    })
    authService.resetPasswordSignin = undefined
    return result
  }

  public forgotPassword = async (email: string): Promise<ResetPasswordOutput> => {
    const result = await resetPassword({ username: email })
    return result
  }

  public getUserId = async () => {
    return await fetchUserAttributes()
      .then((attributes) => {
        const userId = attributes.sub

        return userId
      })
      .catch((err) => {
        console.error(err)
        return ""
      })
  }
}

const authService = new AuthService()

export default authService
